export interface EventCategory {
  id: number;
  documentId: string;
  name: string;
  slug: string;
  order?: number;
  archived?: boolean;
  createdAt: string;
  updatedAt: string;
  // D&P off post-PR #355: Strapi may omit this field. Optional to match reality.
  publishedAt?: string | null;
}

export interface AgendaItem {
  time: string;
  activity: string;
}

export type EventType = 'one-off' | 'recurring-daily' | 'recurring-weekly' | 'recurring-bi-weekly' | 'recurring-monthly';
export type RecurringEventScope = 'this-event' | 'this-and-future';

type RecurringEventType = Exclude<EventType, 'one-off'>;

export interface RecurringEventMutationContext {
  scope: RecurringEventScope;
}

export interface EventMutationResult {
  success: boolean;
  eventDocumentId?: string;
  error?: string;
}

/**
 * Returns true when the event cadence is recurring (i.e. the row is one
 * occurrence of a series). After the MWBI-684 model change each occurrence is
 * its own row; `eventType` still records the cadence for label purposes.
 */
export function isRecurringEventType(eventType?: EventType): eventType is RecurringEventType {
  switch (eventType) {
    case 'recurring-daily':
    case 'recurring-weekly':
    case 'recurring-bi-weekly':
    case 'recurring-monthly':
      return true;
    case 'one-off':
    case undefined:
      return false;
  }
  // Compile-time exhaustiveness guard: if a new EventType value is added to
  // the union, this assignment fails and forces the developer to explicitly
  // classify it as recurring or non-recurring above.
  const _exhaustive: never = eventType;
  void _exhaustive;
  return false;
}

export interface Event {
  id: number;
  documentId: string;
  title: string;
  date: string;
  endDate?: string | null;
  timeStart: string;
  timeEnd: string;
  location: string;
  /**
   * Free-text cost, shown to members exactly as the organizer wrote it
   * ("Pay what you can", "$25", "Free / Gratis"). Optional because rows that
   * predate MWBI-919 -- and event objects cached in a returning user's browser
   * by the persisted event store -- have no cost yet. Read it through
   * getEventCostLabel(), never directly, so the legacy fallback applies.
   */
  cost?: string | null;
  /** @deprecated Replaced by `cost` (MWBI-919). Read-only legacy fallback; removed in the phase-2 follow-up. */
  price: number | null;
  image?: { id?: number; url: string; alternativeText?: string } | null;
  images?: { id: number; url: string; alternativeText?: string }[] | null;
  description?: string;
  organizer?: string;
  accessibilityNotes?: string;
  agenda?: AgendaItem[];
  categories?: EventCategory[] | null;
  // MWBI-890 — eventListVisible backs the client-side mirror of the server's
  // group-level event gate in getSortedEvents. Optional: legacy rows and any
  // caller that does not request the field leave it undefined, which reads as
  // "showing", matching the server's $null fail-open branch.
  group?: { id: number; documentId: string; name: string; eventListVisible?: boolean | null } | null;
  author?: { id: number } | null;
  attendees?: { id: number }[];
  attendeeCount?: number;
  bookingLink?: string | null;
  eventType?: EventType;
  seriesId?: string | null;
  /** @deprecated Replaced by `cost` (MWBI-919). Read-only legacy fallback; removed in the phase-2 follow-up. */
  isFree?: boolean;
  openToPublic?: boolean;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // D&P off post-PR #355: Strapi may omit this field. Optional to match reality.
  publishedAt?: string | null;
}

export interface EventCategoriesResponse {
  data: EventCategory[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface EventsResponse {
  data: Event[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface EventResponse {
  data: Event;
}

export function getEventCategories(
  event: Pick<Event, 'categories'> | null | undefined,
): EventCategory[] {
  if (!event || !Array.isArray(event.categories)) {
    return [];
  }

  return event.categories;
}

export function getEventCategorySlugs(
  event: Pick<Event, 'categories'> | null | undefined,
): string[] {
  return getEventCategories(event).map((category) => category.slug);
}

